import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, MapPin } from "lucide-react";
import { imgSrc } from "@/lib/api";
import Reveal from "@/components/site/Reveal";

function GallerySlider({ slides, name, testId }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selected, setSelected] = useState(0);

  const onSelect = useCallback(() => {
    if (emblaApi) setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  return (
    <div className="relative h-full" data-testid={testId}>
      <div className="overflow-hidden h-full" ref={emblaRef}>
        <div className="flex h-full">
          {slides.map((s, i) => (
            <div key={i} className="flex-[0_0_100%] min-w-0 h-full">
              <img
                src={imgSrc(s)}
                alt={`${name} — photo ${i + 1}`}
                loading="lazy"
                className="w-full h-full object-cover object-top transition-transform duration-[1.2s] ease-out group-hover:scale-105"
              />
            </div>
          ))}
        </div>
      </div>
      <button
        aria-label="Previous photo"
        onClick={() => emblaApi?.scrollPrev()}
        className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 bg-black/70 backdrop-blur border border-[#C5A059]/40 text-[#C5A059] flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-[#C5A059] hover:text-black transition-all duration-300"
        data-testid={`${testId}-prev`}
      >
        <ChevronLeft size={16} />
      </button>
      <button
        aria-label="Next photo"
        onClick={() => emblaApi?.scrollNext()}
        className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 bg-black/70 backdrop-blur border border-[#C5A059]/40 text-[#C5A059] flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-[#C5A059] hover:text-black transition-all duration-300"
        data-testid={`${testId}-next`}
      >
        <ChevronRight size={16} />
      </button>
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-2" data-testid={`${testId}-dots`}>
        {slides.map((_, i) => (
          <button
            key={i}
            aria-label={`Go to photo ${i + 1}`}
            onClick={() => emblaApi?.scrollTo(i)}
            className={`w-1.5 h-1.5 rotate-45 transition-colors duration-300 ${i === selected ? "bg-[#C5A059]" : "bg-white/40"}`}
            data-testid={`${testId}-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
}

function ProjectCard({ project, index, compact }) {
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  const slides = [project.image, ...(project.gallery || [])].filter(Boolean);

  return (
    <Reveal delay={index * 0.12} className={compact ? "" : index % 2 === 1 ? "lg:mt-24" : ""}>
      <article
        className="spotlight-frame group border border-[#C5A059]/20 bg-[#0A0A0A] p-4 sm:p-6 hover:border-[#C5A059]/50 transition-colors duration-500"
        onMouseMove={onMove}
        data-testid={`project-card-${project.id}`}
      >
        <div className={`overflow-hidden relative ${compact ? "aspect-[4/3]" : "aspect-[4/5] sm:aspect-[3/4]"}`}>
          {slides.length > 1 ? (
            <GallerySlider slides={slides} name={project.name} testId={`project-gallery-${project.id}`} />
          ) : (
            <img
              src={imgSrc(project.image)}
              alt={project.name}
              loading="lazy"
              className="w-full h-full object-cover object-top transition-transform duration-[1.2s] ease-out group-hover:scale-105"
            />
          )}
          <span className="absolute top-4 left-4 z-10 font-accent text-[10px] tracking-[0.25em] uppercase bg-black/70 backdrop-blur px-3 py-1.5 border border-[#C5A059]/40 text-[#C5A059]" data-testid={`project-status-${project.id}`}>
            {project.status === "ongoing" ? "Now Rising" : "Delivered"}
          </span>
          <span className="absolute bottom-4 right-4 font-display text-5xl text-white/20 group-hover:text-[#C5A059]/50 transition-colors duration-500">
            {String(index + 1).padStart(2, "0")}
          </span>
        </div>
        <div className="pt-6 pb-2 px-1">
          <p className="font-accent text-[10px] tracking-[0.25em] uppercase text-[#C5A059] mb-2">{project.config}</p>
          <Link to={`/projects/${project.id}`}>
            <h3 className="font-display text-2xl sm:text-3xl tracking-tight group-hover:text-[#D4AF37] transition-colors duration-300" data-testid={`project-name-${project.id}`}>
              {project.name}
            </h3>
          </Link>
          <p className="mt-2 flex items-center gap-1.5 text-zinc-500 text-sm" data-testid={`project-location-${project.id}`}>
            <MapPin size={13} className="text-[#C5A059]" /> {project.location}
          </p>
          {project.description && <p className="mt-4 text-zinc-400 text-sm leading-relaxed">{project.description}</p>}
          {project.features?.length > 0 && (
            <ul className="mt-5 space-y-2 border-t border-white/10 pt-5">
              {project.features.map((f, i) => (
                <li key={i} className="flex items-center gap-3 text-sm text-zinc-300">
                  <span className="w-1 h-1 rotate-45 bg-[#C5A059] shrink-0" /> {f}
                </li>
              ))}
            </ul>
          )}
          <Link
            to={`/projects/${project.id}`}
            className="mt-6 inline-flex items-center gap-2 font-accent text-[10px] tracking-[0.25em] uppercase text-[#C5A059] hover:gap-3.5 transition-all duration-300"
            data-testid={`project-details-link-${project.id}`}
          >
            View Full Details <span aria-hidden>→</span>
          </Link>
        </div>
      </article>
    </Reveal>
  );
}

export default function ProjectsSection({ id, index, label, title, projects, emptyText, intro = null, compact = false }) {
  return (
    <section id={id} className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid={`${id}-section`}>
      <Reveal>
        <div className="flex items-end justify-between border-b border-white/10 pb-8 mb-14">
          <div>
            <p className="font-accent text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-3">
              {index} — {label}
            </p>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight" data-testid={`${id}-title`}>
              {title}
            </h2>
          </div>
          <span className="hidden lg:block font-display text-7xl text-white/5 select-none">{String(projects.length).padStart(2, "0")}</span>
        </div>
      </Reveal>
      {intro && (
        <Reveal delay={0.1}>
          <p className="text-zinc-400 text-base sm:text-lg leading-relaxed max-w-3xl -mt-6 mb-14" data-testid={`${id}-intro`}>
            {intro}
          </p>
        </Reveal>
      )}
      {projects.length === 0 ? (
        <Reveal>
          <p className="text-zinc-500 text-base max-w-md border border-dashed border-white/15 p-10" data-testid={`${id}-empty`}>
            {emptyText}
          </p>
        </Reveal>
      ) : (
        <div className={`grid gap-8 lg:gap-12 ${compact ? "sm:grid-cols-2 lg:grid-cols-3" : "md:grid-cols-2"}`}>
          {projects.map((p, i) => (
            <ProjectCard key={p.id} project={p} index={i} compact={compact} />
          ))}
        </div>
      )}
    </section>
  );
}
